import { Box, Checkbox, Typography } from "@mui/material";
import {
  Bell,
  ClipboardList,
  Clock3,
  Mail,
  Printer,
  ShieldCheck,
  Users,
  UsersRound,
} from "lucide-react";
import { SettingEntry } from "../types";
import { passtrackColors } from "../theme";

const SETTING_ICONS: Record<string, typeof Bell> = {
  bell: Bell,
  mail: Mail,
  clock: Clock3,
  printer: Printer,
  clipboard: ClipboardList,
  users: Users,
  "users-round": UsersRound,
  "shield-check": ShieldCheck,
};

interface SettingToggleRowProps {
  entry: SettingEntry;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export default function SettingToggleRow({
  entry,
  checked,
  onChange,
}: SettingToggleRowProps) {
  const Icon = SETTING_ICONS[entry.icon] ?? Bell;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onChange(!checked)}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        width: "100%",
        textAlign: "left",
        p: 2,
        borderRadius: 2,
        cursor: "pointer",
        font: "inherit",
        background: checked ? passtrackColors.cloud : "#fff",
        border: `1.5px solid ${
          checked ? passtrackColors.blue200 : passtrackColors.borderSubtle
        }`,
        transition: "background-color 120ms ease, border-color 120ms ease",
        "&:hover": {
          borderColor: checked
            ? passtrackColors.blue
            : passtrackColors.borderDefault,
        },
      }}
    >
      <Checkbox
        checked={checked}
        tabIndex={-1}
        disableRipple
        onClick={(e) => e.stopPropagation()}
        onChange={(_, next) => onChange(next)}
        sx={{
          p: 0.25,
          mt: 0.15,
          color: passtrackColors.slate,
          "&.Mui-checked": { color: passtrackColors.blue },
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            mb: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                color: checked
                  ? passtrackColors.blue
                  : passtrackColors.textMuted,
                flexShrink: 0,
              }}
            >
              <Icon size={18} strokeWidth={2} />
            </Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
                color: passtrackColors.ink,
                lineHeight: 1.35,
              }}
            >
              {entry.label}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: checked
                ? passtrackColors.blue
                : passtrackColors.textMuted,
              flexShrink: 0,
              pt: 0.15,
            }}
          >
            {checked ? "Activa" : "Inactiva"}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: 12.5,
            lineHeight: 1.45,
            color: passtrackColors.textMuted,
            pl: 3.5,
          }}
        >
          {entry.description}
        </Typography>
      </Box>
    </Box>
  );
}
